import type {
  ArtikelAnsicht,
  Auslastung,
  AuftragAnsicht,
  EinsatzAendern,
  EinsatzAnlegen,
  EinsatzAnsicht,
  EinsatzStatus,
  Einrichtung,
  Gruppenabgleich,
  Spiegelabgleich,
  Gruppenrolle,
  GruppenrolleAendern,
  GruppenrolleAnlegen,
  Gesellschaft,
  Ich,
  Lauf,
  Rapport,
  VersandListe,
  RapportAendern,
  RapportAnlegen,
  RapportKopf,
  RapportStatus,
  TaetigkeitsartAendern,
  TaetigkeitsartAnlegen,
  TaetigkeitsartAnsicht,
  Uebergang,
  Unterschrift,
  UmsetzungListe,
} from '@arbeitsrapporte/shared';
import { holeToken } from './anmeldung';

/**
 * Der Zugang zur API – ein Wrapper um `fetch`, der das Token mitgibt und
 * die Fehlerform des Hauses (`{ fehler, meldung }`) in eine Ausnahme
 * übersetzt. Mehr nicht: Die Typen kommen aus `@arbeitsrapporte/shared`,
 * geprüft wird auf der Seite des Dienstes.
 */
export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    meldung: string,
    readonly details?: unknown,
  ) {
    super(meldung);
    this.name = 'ApiFehler';
  }
}

async function anfrage<T>(pfad: string, init: RequestInit = {}): Promise<T> {
  const token = await holeToken();
  const antwort = await fetch(`/api${pfad}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  });

  if (!antwort.ok) {
    const koerper = (await antwort.json().catch(() => null)) as {
      fehler?: string;
      meldung?: string;
      details?: unknown;
    } | null;

    throw new ApiFehler(
      antwort.status,
      koerper?.fehler ?? 'unerwartet',
      koerper?.meldung ?? `Die API antwortet mit ${antwort.status}.`,
      koerper?.details,
    );
  }

  return (await antwort.json()) as T;
}

function abfrage(werte: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();

  for (const [k, v] of Object.entries(werte)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }

  const s = p.toString();

  return s ? `?${s}` : '';
}

export const api = {
  ich: () => anfrage<Ich>('/ich'),

  rapporte: (filter: { status?: RapportStatus[]; suche?: string; mitarbeiterId?: string } = {}) =>
    anfrage<{ daten: RapportKopf[]; gesamt: number }>(
      `/rapport${abfrage({ status: filter.status?.join(','), suche: filter.suche, mitarbeiterId: filter.mitarbeiterId, grenze: 200 })}`,
    ),
  rapport: (id: string) => anfrage<Rapport>(`/rapport/${id}`),
  legeAn: (eingabe: RapportAnlegen) =>
    anfrage<Rapport>('/rapport', { method: 'POST', body: JSON.stringify(eingabe) }),
  aendere: (id: string, eingabe: RapportAendern) =>
    anfrage<Rapport>(`/rapport/${id}`, { method: 'PUT', body: JSON.stringify(eingabe) }),
  unterschreibe: (id: string, u: Unterschrift) =>
    anfrage<Rapport>(`/rapport/${id}/unterschrift`, { method: 'PUT', body: JSON.stringify(u) }),
  entferneUnterschrift: (id: string) =>
    anfrage<Rapport>(`/rapport/${id}/unterschrift`, { method: 'DELETE' }),
  /** Ein Übergang – beim Zurückgeben und Stornieren mit Grund, der am Rapport stehen bleibt. */
  uebergang: (id: string, u: Uebergang, grund?: string) =>
    anfrage<Rapport>(`/rapport/${id}/${u}`, {
      method: 'POST',
      ...(grund !== undefined ? { body: JSON.stringify({ grund }) } : {}),
    }),

  /* Die Disposition: planen, ändern, absagen, und die Zusage eintragen. */
  einsaetze: (filter: { status?: EinsatzStatus[]; von?: string; bis?: string } = {}) =>
    anfrage<{ daten: EinsatzAnsicht[]; gesamt: number }>(
      `/einsatz${abfrage({ status: filter.status?.join(','), von: filter.von, bis: filter.bis, grenze: 200 })}`,
    ),
  /** Was die Auftragsabwicklung zur Umsetzung freigegeben hat – nur Leitung. */
  umsetzung: () => anfrage<UmsetzungListe>('/umsetzung'),
  auslastung: (von: string, bis: string) =>
    anfrage<Auslastung>(`/auslastung${abfrage({ von, bis })}`),
  planeEinsatz: (eingabe: EinsatzAnlegen) =>
    anfrage<EinsatzAnsicht>('/einsatz', { method: 'POST', body: JSON.stringify(eingabe) }),
  aendereEinsatz: (id: string, eingabe: EinsatzAendern) =>
    anfrage<EinsatzAnsicht>(`/einsatz/${id}`, { method: 'PATCH', body: JSON.stringify(eingabe) }),
  sageEinsatzAb: (id: string, grund: string) =>
    anfrage<EinsatzAnsicht>(`/einsatz/${id}/absage`, {
      method: 'POST',
      body: JSON.stringify({ grund }),
    }),
  /**
   * Der Rapport zum Einsatz – Auftrag, Ort, Ansprechpartner und Tätigkeit
   * kommen mit. `datum` ist der Tag des Geräts: Der Techniker schreibt am
   * Abend, und der Server rechnet in UTC.
   */
  rapportAusEinsatz: (id: string, datum: string) =>
    anfrage<Rapport>(`/einsatz/${id}/rapport`, { method: 'POST', body: JSON.stringify({ datum }) }),
  trageAntwortEin: (id: string, antwort: string, notiz: string) =>
    anfrage<EinsatzAnsicht>(`/einsatz/${id}/antwort`, {
      method: 'POST',
      body: JSON.stringify({ antwort, notiz }),
    }),
  ansprechpartner: (kontaktId: string) =>
    anfrage<
      {
        id: string;
        name: string;
        email: string;
        telefon: string;
        funktion: string;
        istHaupt: boolean;
      }[]
    >(`/ansprechpartner${abfrage({ kontaktId })}`),
  mitarbeiter: () =>
    anfrage<{ id: string; name: string; email: string; standardTaetigkeitsartId: string | null }[]>(
      '/mitarbeiter',
    ),

  /* Tätigkeitsarten: die Zuordnung Rolle → Artikelnummer, je Gesellschaft. */
  taetigkeitsarten: (gesellschaft?: Gesellschaft, mitDeaktivierten = false) =>
    anfrage<TaetigkeitsartAnsicht[]>(
      `/taetigkeitsart${abfrage({ gesellschaft, mitDeaktivierten: mitDeaktivierten ? 'true' : undefined })}`,
    ),
  legeTaetigkeitsartAn: (eingabe: TaetigkeitsartAnlegen) =>
    anfrage<TaetigkeitsartAnsicht>('/taetigkeitsart', {
      method: 'POST',
      body: JSON.stringify(eingabe),
    }),
  aendereTaetigkeitsart: (id: string, eingabe: TaetigkeitsartAendern) =>
    anfrage<TaetigkeitsartAnsicht>(`/taetigkeitsart/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(eingabe),
    }),
  setzeStandardTaetigkeitsart: (mitarbeiterId: string, taetigkeitsartId: string | null) =>
    anfrage<{ gesetzt: boolean }>(`/mitarbeiter/${mitarbeiterId}/taetigkeitsart`, {
      method: 'PUT',
      body: JSON.stringify({ taetigkeitsartId }),
    }),

  /**
   * Die Druckansicht braucht das Token, ein einfacher Link trüge es nicht.
   * Deshalb holen und in einem neuen Fenster anzeigen – von dort druckt der
   * Browser, oder sichert als PDF.
   */
  oeffneDruck: async (id: string) => {
    /*
     * Das Fenster **zuerst**, noch in der Klick-Geste: Safari und Chrome
     * auf dem Handy blocken ein `window.open` nach einem `await` als
     * Popup – und das Handy ist das Gerät, für das es diese Seite gibt.
     */
    const fenster = window.open('', '_blank');

    if (!fenster) {
      throw new Error('Der Browser hat das Druckfenster geblockt.');
    }

    fenster.document.write('<!doctype html><title>Druckansicht</title><p>Lädt …</p>');

    try {
      const token = await holeToken();
      const antwort = await fetch(`/api/rapport/${id}/druck`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!antwort.ok) {
        throw new ApiFehler(
          antwort.status,
          'unerwartet',
          `Die Druckansicht antwortet mit ${antwort.status}.`,
        );
      }

      const html = await antwort.text();

      fenster.document.open();
      fenster.document.write(html);
      fenster.document.close();
    } catch (fehler) {
      fenster.close();
      throw fehler;
    }
  },

  auftraege: (suche: string, gesellschaft?: Gesellschaft | '') =>
    anfrage<Omit<AuftragAnsicht, 'positionen'>[]>(
      `/auftrag${abfrage({ suche, gesellschaft, grenze: 30 })}`,
    ),
  auftrag: (id: string) => anfrage<AuftragAnsicht>(`/auftrag/${id}`),
  artikel: (suche: string, gesellschaft: Gesellschaft) =>
    anfrage<ArtikelAnsicht[]>(`/artikel${abfrage({ suche, gesellschaft, grenze: 20 })}`),

  versand: () => anfrage<VersandListe>('/versand'),
  versandErneut: (id: string) => anfrage<unknown>(`/versand/${id}/erneut`, { method: 'POST' }),
  /** Wie der Dienst eingerichtet ist – nur Leitung, nur lesend, ohne Geheimnisse. */
  einrichtung: () => anfrage<Einrichtung>('/einrichtung'),
  /** Den Gruppenabgleich jetzt laufen lassen, statt auf die volle Stunde zu warten. */
  /** Eine Entra-Gruppe einer Rolle zuordnen – nur Admin. Antwortet mit allen Zuordnungen. */
  ordneGruppeZu: (eingabe: GruppenrolleAnlegen) =>
    anfrage<Gruppenrolle[]>('/gruppenrolle', { method: 'POST', body: JSON.stringify(eingabe) }),
  aendereGruppenzuordnung: (id: string, eingabe: GruppenrolleAendern) =>
    anfrage<Gruppenrolle[]>(`/gruppenrolle/${id}`, {
      method: 'PUT',
      body: JSON.stringify(eingabe),
    }),
  entferneGruppenzuordnung: (id: string) =>
    anfrage<Gruppenrolle[]>(`/gruppenrolle/${id}`, { method: 'DELETE' }),
  gruppenabgleich: () => anfrage<Gruppenabgleich>('/gruppenabgleich', { method: 'POST' }),
  /** Der jüngste Spiegel-Lauf je Liste. */
  lauf: () => anfrage<Lauf[]>('/lauf'),
  /** Den Spiegel jetzt aus dem Hub nachführen, statt auf die volle Stunde zu warten. */
  spiegelabgleich: () => anfrage<Spiegelabgleich>('/spiegelabgleich', { method: 'POST' }),
};
